import { Monitor } from "../../../../types/assessment/monitor.ts";

// Azure per-privilege usage is classified from Activity Log, which records
// management-plane writes only: data actions, /read actions, and unexpanded
// wildcards are classified "unknown" (never "unused"), so the usage-based
// monitors below cannot flag them. See classifyPrivilegeUsage in
// backend/src/integrations/resources/azure/assessment/builders/role.ts.
export const AzureGrantPresets = {
  "Unused Azure Role Assignments": Monitor({
    show: "grant",
    scopes: ["azure"],
    searchTerm:
      'usage:type:"unused" ^usage:type:!"unused" ^grant=isProviderManaged:"true" identity=status:"active"',
    label: "Unused Azure role assignment",
    priority: "HIGH",
    cta: "No privileges given by this role assignment have been used in the last 90 days. Consider removing this role assignment.",
    remediation: (action: string) =>
      `No privileges given by this role assignment have been used in the last 90 days. P0 has automatically ${action} to remove this role assignment.`,
    description:
      "Role assignments for which no privileges have been used in the last 90 days.",
  }),
  "Excessive Azure Role Assignments": Monitor({
    scopes: ["azure"],
    show: "grant",
    // nodes that can reach an unused permission AND can reach a used or unknown permission
    searchTerm:
      'usage:type:"unused" usage:type:!"unused" ^grant=isProviderManaged:"true"',
    label: "Excessive Azure role assignments",
    priority: "MEDIUM",
    cta: "This role assignment has more privileges than are required for its intended use. Consider replacing this role assignment with a least-privileged role assignment.",
    description:
      "Role assignments with more privileges than are required for their intended use.",
  }),
  "Azure Role Assignments with Unused Privileged Access": Monitor({
    scopes: ["azure"],
    show: "grant",
    searchTerm:
      'usage:type:"unused"->risk:"CRITICAL" ^grant=isProviderManaged:"true"',
    label: "Azure role assignments with unused privileged access",
    priority: "CRITICAL",
    cta: "This role assignment conveys privileges that allow critical risks, and these privileges have not been used in the last 90 days. Consider replacing this role assignment with a role that does not include these privileges.",
    description:
      "Role assignments with unused privileges that allow sensitive access.",
  }),
  "Privileged Azure User Role Assignments": Monitor({
    scopes: ["azure"],
    show: "grant",
    searchTerm:
      'grant=principalType:"user" risk:"CRITICAL" ^grant=isProviderManaged:"true"',
    label: "Privileged Azure user role assignment",
    priority: "MEDIUM",
    cta: "This user role assignment allows access with critical risks. Consider replacing this role assignment with a low-sensitivity role assignment, and using just-in-time access for ephemeral access.",
    description:
      "User role assignments with any privileges that allow sensitive access.",
  }),
  "Privileged Azure Group Role Assignments": Monitor({
    scopes: ["azure"],
    show: "grant",
    searchTerm:
      'grant=principalType:"group" risk:"CRITICAL" ^grant=isProviderManaged:"true"',
    label: "Privileged Azure group role assignment",
    priority: "MEDIUM",
    cta: "This group role assignment allows access with critical risks. Consider replacing this role assignment with a low-sensitivity role assignment, and using just-in-time access for ephemeral access.",
    description:
      "Group role assignments with any privileges that allow sensitive access.",
  }),
  "Privileged Azure Service Principal Role Assignments": Monitor({
    scopes: ["azure"],
    show: "grant",
    searchTerm:
      'grant=principalType:"service-account" risk:"CRITICAL" ^grant=isProviderManaged:"true"',
    label: "Privileged Azure service principal role assignment",
    priority: "MEDIUM",
    cta: "This service principal role assignment allows access with critical risks. Consider replacing this role assignment with a low-sensitivity role assignment, and using just-in-time access for ephemeral access.",
    description:
      "Service principal role assignments with any privileges that allow sensitive access.",
  }),
  "Privileged Azure Role Assignments": Monitor({
    scopes: ["azure"],
    show: "grant",
    searchTerm: 'risk:"CRITICAL" ^grant=isProviderManaged:"true"',
    label: "Privileged Azure role assignment",
    priority: "MEDIUM",
    cta: "This role assignment allows access with critical risks. Consider replacing this role assignment with a low-sensitivity role assignment, and using just-in-time access for ephemeral access.",
    description:
      "Role assignments with any privileges that allow sensitive access.",
  }),
};
